/**
 * The escape from the timber hard-lock.
 *
 * The owner's report: *"you can also hard lock yourself with wood, by not
 * building a timber or building a saw mill etc find a way to solve that as
 * well"*. It is real, and it is not a slow death — it is an instant one that
 * takes an hour to become visible.
 *
 * Every scrap of timber in the game comes out of a building: `grep` for grants
 * of `'timber'` and there is exactly one outside the market and the Chronicle,
 * the 5% salvage from a demolition. So the chain closes on itself. A
 * Woodcutter's Camp costs 25 timber, which means the root resource gates its
 * own root building; with no camp standing and fewer than 25 in the stores,
 * nothing you can do inside the settlement will ever produce another stick.
 * Demolition is not the way out either — 5% of a house's 30 timber is 1.5, so
 * clearing seventeen houses buys one camp, and by then there is no town left to
 * put it in.
 *
 * `COORDINATION.md` names the market as the escape hatch — *"that is the
 * deadlock escape if your last Wellspring is rubble"* — and for every other
 * resource it is. It cannot be for this one. A Market costs 70 timber and 20
 * planks, so a settlement that is short of timber cannot build the building
 * that sells it timber, and a settlement that has one but no coin is just as
 * finished. The hatch that only opens when you are not locked is not a hatch.
 *
 * So: **deadfall**. When the town provably cannot ever cut wood again, the
 * villagers go out and pick it up off the ground, slowly, until there is
 * exactly enough for a camp — and then they stop, because this is a way out and
 * not an income.
 *
 * Three properties it is built to have:
 *
 * - **It cannot touch a settlement that is not locked.** Every branch of
 *   `timberLock` is an escape that is actually open: a camp standing anywhere
 *   (finished or still a foundation), enough timber in the stores, or a market
 *   and the coin to use it. A normal run never reaches the grant, which is why
 *   this changes no balance number and no siege digest.
 * - **It stops at the threshold, not at a timer.** The trickle is clamped to
 *   the shortfall, so the moment the stores can pay for a camp the lock reads
 *   open and the grant stops on its own. Nothing needs to remember that it ran.
 * - **It says so.** Half of the owner's complaint is that the settlement is
 *   over "with no message saying so". A locked town is told what has happened
 *   and what to do about it, on a cadence, until it is not locked.
 *
 * It lives in its own module and is called from `stepWorld` rather than from
 * `stepSettlement` for one boring reason: it needs `hasMarket` and
 * `marketPrice` from `apply.ts`, and `apply.ts` already imports
 * `settlement.ts`. Calling it from the step keeps the arrow pointing one way.
 */
import { TICK_RATE } from '../constants.ts';
import { LogTone, pushLog } from '../events.ts';
import { Team, type ResourceKey } from '../types.ts';
import type { World } from '../world.ts';
import { buildingType, BUILDING_TYPES } from '../../data/buildings.ts';
import { availableBuildings, hasMarket, marketPrice } from '../apply.ts';
import { grantCapped } from './settlement.ts';

/**
 * Timber per second, by hand.
 *
 * A Woodcutter's Camp makes 0.42/s at full staffing, so this is a shade under
 * half of one camp and it is meant to be felt: 25 timber — one camp — takes
 * a little over two minutes of nothing else going right. Fast enough that a
 * locked settlement is a setback rather than a dead save, slow enough that
 * nobody will ever tear down their camps to farm it.
 */
const DEADFALL_PER_SECOND = 0.2;

/** How often a locked settlement is reminded, in seconds. */
const DEADFALL_LOG_SECONDS = 20;

/** Does this building type ever put timber in the stores? */
function makesTimber(typeId: number): boolean {
  return (buildingType(typeId).produces.timber ?? 0) > 0;
}

/**
 * The shortfall, or 0 if the settlement is not locked.
 *
 * Ordered cheapest test first, and the first test is the one that is true in
 * every ordinary run, so this costs a settlement with a camp in it one pass
 * over the building array and nothing else.
 */
export function timberLock(w: World): number {
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0 || b.team[i] !== Team.Player) continue;
    // A foundation counts. It was already paid for, and it will finish.
    if (makesTimber(b.typeId[i])) return 0;
  }

  /**
   * The cheapest camp the player is allowed to place *right now* — read off
   * `availableBuildings` rather than hard-coding `'woodcutter'`, so a faction
   * or an Age that cuts wood some other way is served by the same rule and a
   * building added later cannot quietly fall outside it.
   */
  let need = Number.POSITIVE_INFINITY;
  for (const idx of availableBuildings(w)) {
    const def = BUILDING_TYPES[idx];
    if ((def.produces.timber ?? 0) <= 0) continue;
    need = Math.min(need, def.cost.timber ?? 0);
  }
  // Nothing that cuts wood is available at all: not a lock this can pick.
  if (!Number.isFinite(need)) return 0;

  const short = need - w.res.timber;
  if (short <= 0) return 0;

  // The market, if it is genuinely open. `marketPrice` already carries the
  // spread and the glut discount, so this is the price the player would pay.
  if (hasMarket(w)) {
    const unit = marketPrice(w, 'timber' as ResourceKey, false);
    if (unit > 0 && w.res.gold >= short * unit) return 0;
  }

  return short;
}

/**
 * Pick the deadfall up, and say that it is being picked up.
 *
 * Deterministic and stateless: the grant is a function of `dt` and the
 * shortfall, and the reminder is a function of the tick. Nothing here reads a
 * clock or an `Rng`, and nothing new is hashed or saved.
 */
export function stepDeadfall(w: World, dt: number): void {
  if (w.gameOver) return;
  // Somebody has to bend down and pick it up. This also keeps a dead town dead.
  if (w.counts.villagers <= 0) return;

  const short = timberLock(w);
  if (short <= 0) return;

  grantCapped(w, 'timber', Math.min(short, DEADFALL_PER_SECOND * dt));

  if (w.tick % (TICK_RATE * DEADFALL_LOG_SECONDS) === 0) {
    pushLog(
      w.events,
      'No one is cutting wood, and there is not enough left to raise a camp. They are out gathering deadfall by hand — enough for one camp, and no more. Build it.',
      LogTone.Bad,
    );
  }
}
